import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  MultiSelect,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  cakisanBasamakAdlari,
  ZINCIR_BASAMAK_SAPKALARI,
  ZINCIR_EK_BASAMAK_SAPKASI,
  ZINCIR_VARSAYILAN_BASAMAK_SAPKASI,
} from '@isg/validation';
import type { ApprovalChainDetail, RoleType } from '@isg/shared-types';
import {
  useOnayZincirleri,
  useWorkplacesTumu,
  useZincirGuncelle,
  useZincirOlustur,
  useZincirSablonu,
  useZincirSil,
} from '../api/queries';
import { SAPKA_ETIKET } from '../i18n/tr';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * ONAY ZİNCİRİ TANIMI (F3·S2 — Kapsam §16.7 v2, K4; §20.5: yalnız holding
 * yöneticisi). Tanım ÜÇ ADIMDIR ve tek formda biter:
 *
 *   1) şapka seç  2) izin veren işaretle  3) işyerlerine ata
 *
 * Kişi seçilmez (havuz lokasyondan türetilir), sıra yoktur (basamaklar
 * PARALEL koşar), koşul yoktur (dallanma kapsam dışı). "Tam bir izin veren"
 * kuralını ekran da zorlar, sunucu da (K3 — çift kilit).
 *
 * BAŞLANGIÇ ŞABLONU sistem ÖNERİSİDİR: tek tuşla alan sorumlusu = izin veren
 * zinciri kurar; K4 gereği hiçbir işyerine kendiliğinden kurulmaz.
 */

/** Zincir basamağı olabilecek şapkalar — ALAN SÖZLÜĞÜ `@isg/validation`dan
 *  (`ZINCIR_BASAMAK_SAPKALARI`; P13-b — ekran liste yazmaz). */
const BASAMAK_SAPKALARI: readonly RoleType[] = ZINCIR_BASAMAK_SAPKALARI;

interface BasamakTaslagi {
  ad: string;
  sapka: RoleType;
}

/**
 * P-3 (28.08.2026 — madde 123): BASAMAK ADI ŞAPKADAN TÜRER.
 *
 * Eskiden "Basamak ekle" her satırı SABİT "İSG uzmanı onayı" adıyla açıyordu
 * ve şapka seçici ada DOKUNMUYORDU: iki basamak aynı adı taşıyabiliyor,
 * onaycının "bekleyen onaylarım" listesinde iki ÖZDEŞ satır beliriyordu.
 *
 * ⚠️ SERBEST METİN İLKESİ KORUNUR (müşterinin kendi prosedür dili): ad
 * yalnız KULLANICI ELLE DEĞİŞTİRMEMİŞSE tazelenir — `adElleMi` bunu
 * durum tutmadan, adı o şapkanın varsayılanıyla karşılaştırarak anlar.
 */
function basamakVarsayilanAdi(sapka: RoleType): string {
  return `${SAPKA_ETIKET[sapka]} onayı`;
}

function adElleMi(basamak: BasamakTaslagi): boolean {
  return basamak.ad.trim() !== basamakVarsayilanAdi(basamak.sapka);
}

/** Şapka değişince ad tazelenir (elle değiştirilmemişse) — tek yerde. */
function sapkaDegistir(basamak: BasamakTaslagi, sapka: RoleType): BasamakTaslagi {
  return { sapka, ad: adElleMi(basamak) ? basamak.ad : basamakVarsayilanAdi(sapka) };
}

/** Çakışma kuralının TEK tanımı @isg/validation'dadır (motor da onu çağırır);
 *  ekran
 *  yalnız aynı fonksiyonu okuyup düğmeyi kapatır ve sebebi YAZAR. */
/**
 * K1.4 (tur 10) — "BU BASAMAĞI KAÇ KİŞİ ONAYLAYABİLİR".
 *
 * Kurulum kararı burada görünür olur: K1.1'den sonra onay havuzu **yalnız
 * elle verilmiş bahçeyi** okur (kişinin o işyerinde çalışıyor olması onay
 * yetkisi doğurmaz), dolayısıyla zincirdeki her şapkaya elle bahçe atamak
 * fiilen zorunlu bir kurulum adımıdır. O adım atlanırsa **talebi yazan kişi**
 * gönderim anında 422 alır — kurulumu yapan ise hiçbir şey görmezdi.
 *
 * ⚠️ **UYARI DEĞİL, ENGEL DE DEĞİL** (ürün sahibi kararı): satır yalnız
 * söyler; düzeltmeyi kurulumu yapan kendi yapar. Fail-loud kapı gönderimde
 * (K4) zaten duruyor.
 *
 * ⚠️ **İKİ SAYI, TEK CÜMLE.** Yalnız "N kişi" yazmak §2.40'ın "yalan söyleyen
 * kontrol" sınıfına girerdi: dal bahçeli bir alan sorumlusu işyerinin yalnız
 * bir dalında onaylayabilir ve komşu dalda açılan talep yine gönderilemez.
 */
function BasamakHavuzNotu({
  step,
}: {
  step: ApprovalChainDetail['steps'][number];
}): React.ReactElement {
  if (step.havuzSayisi === 0) {
    return (
      <Text size="xs" c={ANLAM.engel} data-testid="basamak-havuz">
        Onaylayabilecek kimse yok — bu işyerinde izin talebi gönderilemez
      </Text>
    );
  }
  if (step.tamKapsayanSayisi === 0) {
    return (
      <Text size="xs" c={ANLAM.dikkat} data-testid="basamak-havuz">
        {step.havuzSayisi} kişi onaylayabilir — yalnız kendi bahçelerindeki lokasyonlarda; işyerinin
        tamamını kapsayan yok
      </Text>
    );
  }
  return (
    <Text size="xs" c="dimmed" data-testid="basamak-havuz">
      {step.havuzSayisi} kişi onaylayabilir
    </Text>
  );
}

function cakismaNotu(basamaklar: BasamakTaslagi[]): string | null {
  const cakisan = cakisanBasamakAdlari(basamaklar);
  return cakisan.length === 0
    ? null
    : `Aynı zincirde iki basamak aynı adı taşıyamaz: ${cakisan.join(' · ')}`;
}

export function OnayZincirleriScreen(): React.JSX.Element {
  const zincirler = useOnayZincirleri();
  const [formAcik, setFormAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState<ApprovalChainDetail | null>(null);
  const sil = useZincirSil();

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>PTW onay zincirleri</Title>
        <Button data-testid="zincir-ac" onClick={() => setFormAcik(true)}>
          Yeni zincir
        </Button>
      </Group>
      <Text size="sm" c="dimmed">
        Zincir merkezî prosedürdür: holding tanımlar, işyerine atanır. Basamaklar{' '}
        <strong>paralel</strong> koşar — hepsi onaylayınca izin açılır. Zinciri olmayan işyerinde
        izin talebi gönderilemez.
      </Text>
      <QueryStatus queries={[zincirler]} />
      {zincirler.isSuccess && zincirler.data.length === 0 && (
        <BosListeNotu mesaj="Tanımlı onay zinciri yok — izin talepleri gönderilemez." />
      )}
      {zincirler.isSuccess && zincirler.data.length > 0 && (
        <Table striped highlightOnHover data-testid="zincir-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>Zincir</Table.Th>
              <Table.Th>Basamaklar (paralel)</Table.Th>
              <Table.Th>İşlem</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {zincirler.data.map((zincir) => (
              <Table.Tr key={zincir.id} data-testid="zincir-satiri">
                <Table.Td>{zincir.workplaceAd}</Table.Td>
                <Table.Td>{zincir.ad}</Table.Td>
                <Table.Td>
                  <Stack gap={6}>
                    {zincir.steps.map((step) => (
                      <Group key={step.id} gap="xs" wrap="nowrap">
                        <Badge
                          variant={step.izinVeren ? 'filled' : 'light'}
                          data-testid="zincir-basamagi"
                        >
                          {SAPKA_ETIKET[step.sapka]}
                          {step.izinVeren ? ' · izin veren' : ''}
                        </Badge>
                        <BasamakHavuzNotu step={step} />
                      </Group>
                    ))}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button size="xs" variant="default" onClick={() => setDuzenlenen(zincir)}>
                      Düzenle
                    </Button>
                    <ActionIcon
                      variant="subtle"
                      color={ANLAM.engel}
                      aria-label="Zinciri kaldır"
                      data-testid="zincir-sil"
                      loading={sil.isPending}
                      onClick={() => sil.mutate({ id: zincir.id })}
                    >
                      ×
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {sil.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert">
          {sil.error.message}
        </Text>
      )}
      <ZincirFormu opened={formAcik} onClose={() => setFormAcik(false)} />
      <ZincirDuzenleme zincir={duzenlenen} onClose={() => setDuzenlenen(null)} />
    </Stack>
  );
}

function ZincirFormu({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}): React.JSX.Element {
  // ✏️ TUR 9 / B4 (T9-18): şirket başına fan-out yerine TEK istek.
  const workplaces = useWorkplacesTumu().data ?? [];
  const olustur = useZincirOlustur();
  const sablon = useZincirSablonu();
  const [ad, setAd] = useState('');
  const [workplaceIds, setWorkplaceIds] = useState<string[]>([]);
  const [basamaklar, setBasamaklar] = useState<BasamakTaslagi[]>([
    {
      ad: basamakVarsayilanAdi(ZINCIR_VARSAYILAN_BASAMAK_SAPKASI),
      sapka: ZINCIR_VARSAYILAN_BASAMAK_SAPKASI,
    },
  ]);
  const [izinVerenIndex, setIzinVerenIndex] = useState(0);
  const cakisma = cakismaNotu(basamaklar);
  const hazir =
    ad.trim() !== '' &&
    workplaceIds.length > 0 &&
    basamaklar.length > 0 &&
    basamaklar.every((basamak) => basamak.ad.trim() !== '') &&
    cakisma === null &&
    izinVerenIndex < basamaklar.length;

  return (
    <Modal opened={opened} onClose={onClose} title="Yeni onay zinciri" size="lg">
      <Stack>
        <TextInput
          label="Zincir adı"
          description="Müşterinin kendi prosedür adı"
          required
          data-testid="zincir-ad"
          value={ad}
          onChange={(event) => setAd(event.currentTarget.value)}
        />

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            1-2. adım: şapkaları seç, izin vereni işaretle
          </Text>
          <Text size="xs" c="dimmed">
            Kişi seçilmez: havuz, izin lokasyonunu kapsayan bahçedeki şapka sahipleridir. Zincirde{' '}
            <strong>tam bir</strong> izin veren basamağı olur.
          </Text>
          <Radio.Group
            value={String(izinVerenIndex)}
            onChange={(deger) => setIzinVerenIndex(Number(deger))}
          >
            <Stack gap="xs">
              {basamaklar.map((basamak, index) => (
                <Group key={index} align="end" wrap="nowrap">
                  <Radio value={String(index)} label="İzin veren" data-testid="zincir-izin-veren" />
                  <Select
                    label="Şapka"
                    data-testid="zincir-sapka"
                    data={BASAMAK_SAPKALARI.map((sapka) => ({
                      value: sapka,
                      label: SAPKA_ETIKET[sapka],
                    }))}
                    value={basamak.sapka}
                    onChange={(deger) => {
                      if (!deger) return;
                      // P-3: ad elle değiştirilmemişse şapkayla TAZELENİR.
                      setBasamaklar((mevcut) =>
                        mevcut.map((satir, i) =>
                          i === index ? sapkaDegistir(satir, deger as RoleType) : satir,
                        ),
                      );
                    }}
                  />
                  <TextInput
                    label="Basamak adı"
                    data-testid="zincir-basamak-ad"
                    value={basamak.ad}
                    onChange={(event) => {
                      const yeni = event.currentTarget.value;
                      setBasamaklar((mevcut) =>
                        mevcut.map((satir, i) => (i === index ? { ...satir, ad: yeni } : satir)),
                      );
                    }}
                  />
                  {basamaklar.length > 1 && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color={ANLAM.engel}
                      onClick={() => {
                        setBasamaklar((mevcut) => mevcut.filter((_, i) => i !== index));
                        setIzinVerenIndex(0);
                      }}
                    >
                      Kaldır
                    </Button>
                  )}
                </Group>
              ))}
            </Stack>
          </Radio.Group>
          <Button
            size="xs"
            variant="default"
            data-testid="zincir-basamak-ekle"
            onClick={() =>
              setBasamaklar((mevcut) => [
                ...mevcut,
                // P-3: SABİT ad yerine şapkanın varsayılanı. Aynı şapkadan
                // ikinci basamak eklenirse ad çakışır ve kullanıcı bunu
                // KAYDETMEDEN ÖNCE görür (aşağıdaki not) — eskiden çakışma
                // ancak onaycının listesinde iki özdeş satır olarak görünürdü.
                {
                  ad: basamakVarsayilanAdi(ZINCIR_EK_BASAMAK_SAPKASI),
                  sapka: ZINCIR_EK_BASAMAK_SAPKASI,
                },
              ])
            }
          >
            Basamak ekle
          </Button>
        </Stack>

        <MultiSelect
          label="3. adım: işyerlerine ata"
          description="Aynı tanım birden çok işyerine atanabilir"
          required
          searchable
          data-testid="zincir-isyerleri"
          data={workplaces
            .filter((isyeri) => isyeri.durum === 'active')
            .map((isyeri) => ({ value: isyeri.id, label: isyeri.ad }))}
          value={workplaceIds}
          onChange={setWorkplaceIds}
        />

        {cakisma !== null && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="zincir-ad-cakismasi">
            {cakisma}
          </Text>
        )}
        {(olustur.isError || sablon.isError) && (
          <Text size="sm" c={ANLAM.engel} role="alert">
            {(olustur.error ?? sablon.error)?.message}
          </Text>
        )}
        <Group justify="space-between">
          <Button
            variant="default"
            data-testid="zincir-sablon"
            disabled={workplaceIds.length === 0}
            loading={sablon.isPending}
            onClick={() => sablon.mutate({ workplaceIds }, { onSuccess: onClose })}
          >
            Başlangıç şablonunu kur
          </Button>
          <Button
            data-testid="zincir-kaydet"
            disabled={!hazir}
            loading={olustur.isPending}
            onClick={() => {
              if (!hazir) return;
              olustur.mutate(
                {
                  workplaceIds,
                  ad: ad.trim(),
                  steps: basamaklar.map((basamak, index) => ({
                    sira: index + 1,
                    ad: basamak.ad.trim(),
                    sapka: basamak.sapka,
                    izinVeren: index === izinVerenIndex,
                  })),
                },
                { onSuccess: onClose },
              );
            }}
          >
            Zinciri kur
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Düzenleme: basamak listesinin TAMAMI değişir (sunucu eskileri arşivler). */
function ZincirDuzenleme({
  zincir,
  onClose,
}: {
  zincir: ApprovalChainDetail | null;
  onClose: () => void;
}): React.JSX.Element {
  const guncelle = useZincirGuncelle();
  const [ad, setAd] = useState('');
  const [basamaklar, setBasamaklar] = useState<BasamakTaslagi[]>([]);
  const [izinVerenIndex, setIzinVerenIndex] = useState(0);
  const [yuklenenId, setYuklenenId] = useState<string | null>(null);
  const cakisma = cakismaNotu(basamaklar);

  // Seçim değişince formu kaydın güncel hâliyle doldur (ekran ikinci
  // doğruluk kaynağı tutmaz — veri sunucudan gelir).
  if (zincir && yuklenenId !== zincir.id) {
    setYuklenenId(zincir.id);
    setAd(zincir.ad);
    setBasamaklar(zincir.steps.map((step) => ({ ad: step.ad, sapka: step.sapka })));
    setIzinVerenIndex(
      Math.max(
        0,
        zincir.steps.findIndex((step) => step.izinVeren),
      ),
    );
  }

  return (
    <Modal
      opened={zincir !== null}
      onClose={onClose}
      title={`Zinciri düzenle — ${zincir?.workplaceAd ?? ''}`}
      size="lg"
    >
      <Stack>
        <TextInput
          label="Zincir adı"
          required
          data-testid="zincir-duzenle-ad"
          value={ad}
          onChange={(event) => setAd(event.currentTarget.value)}
        />
        <Radio.Group
          value={String(izinVerenIndex)}
          onChange={(deger) => setIzinVerenIndex(Number(deger))}
        >
          <Stack gap="xs">
            {basamaklar.map((basamak, index) => (
              <Group key={index} align="end" wrap="nowrap">
                <Radio value={String(index)} label="İzin veren" />
                <Select
                  label="Şapka"
                  data={BASAMAK_SAPKALARI.map((sapka) => ({
                    value: sapka,
                    label: SAPKA_ETIKET[sapka],
                  }))}
                  value={basamak.sapka}
                  onChange={(deger) => {
                    if (!deger) return;
                    // P-3: ad elle değiştirilmemişse şapkayla TAZELENİR.
                    setBasamaklar((mevcut) =>
                      mevcut.map((satir, i) =>
                        i === index ? sapkaDegistir(satir, deger as RoleType) : satir,
                      ),
                    );
                  }}
                />
                <TextInput
                  label="Basamak adı"
                  value={basamak.ad}
                  onChange={(event) => {
                    const yeni = event.currentTarget.value;
                    setBasamaklar((mevcut) =>
                      mevcut.map((satir, i) => (i === index ? { ...satir, ad: yeni } : satir)),
                    );
                  }}
                />
              </Group>
            ))}
          </Stack>
        </Radio.Group>
        {cakisma !== null && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="zincir-duzenle-ad-cakismasi">
            {cakisma}
          </Text>
        )}
        {guncelle.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert">
            {guncelle.error.message}
          </Text>
        )}
        <Button
          data-testid="zincir-duzenle-kaydet"
          loading={guncelle.isPending}
          disabled={!zincir || ad.trim() === '' || basamaklar.length === 0 || cakisma !== null}
          onClick={() => {
            if (!zincir) return;
            guncelle.mutate(
              {
                id: zincir.id,
                input: {
                  ad: ad.trim(),
                  steps: basamaklar.map((basamak, index) => ({
                    sira: index + 1,
                    ad: basamak.ad.trim(),
                    sapka: basamak.sapka,
                    izinVeren: index === izinVerenIndex,
                  })),
                },
              },
              { onSuccess: onClose },
            );
          }}
        >
          Kaydet
        </Button>
      </Stack>
    </Modal>
  );
}
